import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MessageCircle, Mail, Trash2, Search, Phone, MapPin } from "lucide-react";
import { useAdminApi, fmtDate } from "./useAdminApi";

const STATUSES = ["new", "contacted", "booked", "closed"];
const statusColor = { new: "bg-[#00C9B7] text-[#031312]", contacted: "bg-amber-400/20 text-amber-300", booked: "bg-emerald-500/20 text-emerald-300", closed: "bg-slate-600/30 text-slate-400" };

export const PanelHeader = ({ title, sub, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
    <div><h1 className="font-display uppercase text-3xl sm:text-4xl">{title}</h1>{sub && <p className="text-slate-400 mt-1 text-sm">{sub}</p>}</div>
    {children}
  </div>
);

const waReply = (l) => {
  const num = (l.phone || "").replace(/[^\d]/g, "");
  return num ? `https://wa.me/${num}?text=${encodeURIComponent(`Hello ${l.name.split(" ")[0]}, this is Dr. Cora's office. Thank you for your message!`)}` : null;
};

const LeadRow = ({ l, i, onStatus, onDelete }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }} className={`rounded-2xl border p-5 bg-[#131B21] ${l.read ? "border-white/5" : "border-[#00C9B7]/40"}`} data-testid={`lead-row-${l.id}`}>
    <div className="flex flex-col md:flex-row md:items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <p className="font-semibold text-white">{l.name}</p>
          {!l.read && <span className="text-[10px] uppercase font-bold tracking-widest text-[#00C9B7]">New</span>}
          <span className="text-[10px] uppercase tracking-widest text-slate-500 border border-white/10 rounded-full px-2 py-0.5">{l.source}</span>
          {l.pain === "yes" && <span className="text-[10px] uppercase font-bold tracking-widest bg-rose-500/20 text-rose-300 rounded-full px-2 py-0.5" data-testid={`lead-pain-${l.id}`}>In pain</span>}
          {l.pain === "no" && <span className="text-[10px] uppercase tracking-widest text-slate-500">No pain</span>}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-400">
          <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-white"><Mail size={13} /> {l.email}</a>
          {l.phone && <span className="inline-flex items-center gap-1"><Phone size={13} /> {l.phone}</span>}
          {l.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {l.location}</span>}
          <span>{fmtDate(l.created_at)}</span>
        </div>
        {l.services?.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`lead-services-${l.id}`}>{l.services.map((s) => <span key={s} className="text-[11px] rounded-full bg-[#00C9B7]/10 text-[#00C9B7] px-2.5 py-1">{s}</span>)}</div>}
        {l.message && <p className="mt-3 text-sm text-slate-300 whitespace-pre-wrap">{l.message}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <select value={l.status} onChange={(e) => onStatus(l, e.target.value)} className={`rounded-full px-3 py-1.5 text-xs font-semibold uppercase border-0 ${statusColor[l.status]}`} data-testid={`lead-status-select-${l.id}`}>
          {STATUSES.map((s) => <option key={s} value={s} className="bg-[#131B21] text-white">{s}</option>)}
        </select>
        {waReply(l) && <a href={waReply(l)} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full bg-[#25D366]/15 text-[#25D366] flex items-center justify-center hover:bg-[#25D366] hover:text-white" title="Reply on WhatsApp" data-testid={`lead-whatsapp-${l.id}`}><MessageCircle size={16} /></a>}
        <button onClick={() => onDelete(l)} className="w-9 h-9 rounded-full bg-red-500/10 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white" title="Delete" data-testid={`lead-delete-${l.id}`}><Trash2 size={15} /></button>
      </div>
    </div>
  </motion.div>
);

export default function LeadsPanel({ onRead }) {
  const api = useAdminApi();
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/leads").then(setLeads);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async () => { await api.post("/admin/leads/mark-read"); onRead(); load(); };
  const onStatus = async (l, status) => { await api.patch(`/admin/leads/${l.id}`, { status }); load(); };
  const onDelete = async (l) => {
    if (!window.confirm(`Delete lead from ${l.name}?`)) return;
    await api.del(`/admin/leads/${l.id}`);
    toast.success("Lead deleted");
    load();
  };

  const shown = useMemo(() => (leads || []).filter((l) => (filter === "all" || l.status === filter) && `${l.name} ${l.email} ${l.message}`.toLowerCase().includes(q.toLowerCase())), [leads, q, filter]);

  return (
    <div data-testid="leads-panel">
      <PanelHeader title="Leads inbox" sub="Every form on the website lands here. New leads chime and show a badge.">
        <button onClick={markRead} className="rounded-full border border-[#00C9B7]/30 text-[#00C9B7] px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#00C9B7] hover:text-[#031312]" data-testid="leads-mark-read-btn">Mark all read</button>
      </PanelHeader>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1"><Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads" className="w-full bg-[#131B21] border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#00C9B7]" data-testid="leads-search-input" /></div>
        <div className="flex gap-1 bg-[#131B21] rounded-xl p-1 overflow-x-auto">
          {["all", ...STATUSES].map((s) => <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 rounded-lg text-xs uppercase tracking-widest ${filter === s ? "bg-[#00C9B7] text-[#031312] font-bold" : "text-slate-400"}`} data-testid={`leads-filter-${s}`}>{s}</button>)}
        </div>
      </div>
      {leads === null ? <p className="text-slate-500">Loading…</p> : shown.length === 0 ? <p className="text-slate-500 py-16 text-center border border-dashed border-white/10 rounded-2xl" data-testid="leads-empty">No leads yet.</p> : (
        <div className="space-y-3">{shown.map((l, i) => <LeadRow key={l.id} l={l} i={i} onStatus={onStatus} onDelete={onDelete} />)}</div>
      )}
    </div>
  );
}
